import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Unlock, MessageSquare, Camera, ChevronDown, ChevronUp } from 'lucide-react';
import { useGoBack } from '../../hooks/useGoBack';
import { FotoThumbnail } from '../../components/shared/FotoThumbnail';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { StatusBadge } from '../../components/ui/Badge';
import {
  usePlanillaDetail,
  useApprovePlanilla,
  useRejectPlanilla,
  useUnlockPlanilla,
  usePlanillaProgress,
  type PlanillaDetailDTO,
} from '../../hooks/usePlanillas';
import { ProjectProgressCard } from './AdminReviewPage';
import { toast } from '../../components/ui/Toast';

export function GerenteApprovalPage() {
  const { planillaId } = useParams<{ planillaId: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack('/planillas');
  const { data: planilla, isLoading } = usePlanillaDetail(planillaId ?? '');
  const { data: projectProgress } = usePlanillaProgress(planilla?.project_id ?? '');
  const approvePlanilla = useApprovePlanilla(planillaId ?? '');
  const rejectPlanilla = useRejectPlanilla(planillaId ?? '');
  const unlockPlanilla = useUnlockPlanilla(planillaId ?? '');

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectMotivo, setRejectMotivo] = useState('');
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockJustificacion, setUnlockJustificacion] = useState('');

  const handleApprove = async () => {
    try {
      await approvePlanilla.mutateAsync();
      toast('success', `Planilla N°${planilla?.numero} aprobada`);
      navigate('/planillas');
    } catch {
      toast('error', 'Error al aprobar planilla');
    }
  };

  const handleReject = async () => {
    if (rejectMotivo.length < 10) {
      toast('error', 'El motivo debe tener al menos 10 caracteres');
      return;
    }
    try {
      await rejectPlanilla.mutateAsync(rejectMotivo);
      setShowRejectModal(false);
      toast('success', 'Planilla rechazada y devuelta al administrativo');
      navigate('/planillas');
    } catch {
      toast('error', 'Error al rechazar planilla');
    }
  };

  const handleUnlock = async () => {
    if (unlockJustificacion.length < 10) {
      toast('error', 'La justificación debe tener al menos 10 caracteres');
      return;
    }
    try {
      await unlockPlanilla.mutateAsync(unlockJustificacion);
      setShowUnlockModal(false);
      toast('success', 'Planilla desbloqueada');
    } catch {
      toast('error', 'Error al desbloquear planilla');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 fade-up">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!planilla) {
    return <div className="p-4 text-center text-red-600">Planilla no encontrada</div>;
  }

  const canApprove = planilla.estado === 'revisada';
  const canUnlock = planilla.estado === 'aprobada';

  // Group by maestro
  const maestroIds = [...new Set(planilla.details.map((d) => d.maestro_id))];
  const maestroNameMap = new Map<string, string>();
  for (const d of planilla.details) maestroNameMap.set(d.maestro_id, d.maestro_nombre);

  return (
    <div className="space-y-4 fade-up pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={goBack} className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-brand-dark">
            Aprobación — Planilla N°{planilla.numero}
          </h1>
          <p className="text-xs text-gray-500">
            {planilla.project_nombre} · {planilla.fecha_desde} — {planilla.fecha_hasta}
          </p>
        </div>
        <StatusBadge entity="planilla" status={planilla.estado} />
      </div>

      {/* Project-level progress context */}
      {projectProgress && projectProgress.length > 0 && (
        <ProjectProgressCard progress={projectProgress} />
      )}

      {/* Summary table per maestro */}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Maestro</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Bruto</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Descuentos</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Neto</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {maestroIds.map((mId) => {
              const mDetails = planilla.details.filter((d) => d.maestro_id === mId);
              const mDescuentos = planilla.descuentos.filter((d) => d.maestro_id === mId);
              const bruto = mDetails.reduce((s, d) => s + d.subtotal, 0);
              const desc = mDescuentos.reduce((s, d) => s + d.monto, 0);
              const neto = bruto - desc;
              return (
                <tr key={mId}>
                  <td className="px-4 py-3 font-medium text-gray-700">{maestroNameMap.get(mId)}</td>
                  <td className="px-4 py-3 text-right">${bruto.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-red-600">{desc > 0 ? `-$${desc.toFixed(2)}` : '—'}</td>
                  <td className="px-4 py-3 text-right font-bold">${neto.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Per-maestro APU + MDO detail (for review) */}
      {maestroIds.map((maestroId) => (
        <MaestroDetailCard
          key={maestroId}
          maestroId={maestroId}
          maestroNombre={maestroNameMap.get(maestroId) ?? 'Maestro'}
          planilla={planilla}
        />
      ))}

      {/* Totals */}
      <Card className="p-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Total rubros</span>
            <span className="font-medium">${planilla.totals.total_rubros.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Servicios Varios</span>
            <span className="font-medium text-green-700">+${planilla.totals.total_extras.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Total descuentos</span>
            <span className="font-medium">-${planilla.totals.total_descuentos.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between border-t pt-2 font-bold text-lg text-brand-dark">
            <span>Total neto a pagar</span>
            <span>${planilla.totals.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </Card>

      {/* Photo gallery */}
      {planilla.fotos.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Fotos de evidencia ({planilla.fotos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {planilla.fotos.map((f) => (
              <FotoThumbnail key={f.id} s3Key={f.s3_key} filename={f.filename} />
            ))}
          </div>
        </Card>
      )}

      {/* Comments */}
      {planilla.comments.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Comentarios ({planilla.comments.length})
          </h3>
          <div className="space-y-3 max-h-40 overflow-y-auto">
            {planilla.comments.map((c) => (
              <div key={c.id} className="text-sm">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-gray-700">{c.created_by.nombre}</span>
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString('es-EC')}</span>
                </div>
                <p className="text-gray-600">{c.mensaje}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Action buttons */}
      {canApprove && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
          <div className="max-w-4xl mx-auto space-y-3">
            <p className="text-xs text-gray-500 text-center">
              Al aprobar, la planilla quedará bloqueada para edición permanentemente.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="danger" onClick={() => setShowRejectModal(true)}>
                <X className="w-4 h-4 mr-1" />
                Rechazar
              </Button>
              <Button onClick={handleApprove} isLoading={approvePlanilla.isPending}>
                <Check className="w-4 h-4 mr-1" />
                Aprobar Planilla
              </Button>
            </div>
          </div>
        </div>
      )}

      {canUnlock && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
          <div className="max-w-4xl mx-auto flex justify-center">
            <Button variant="secondary" onClick={() => setShowUnlockModal(true)}>
              <Unlock className="w-4 h-4 mr-1" />
              Desbloquear planilla
            </Button>
          </div>
        </div>
      )}

      {/* Reject modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Rechazar planilla">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Indique el motivo del rechazo (mínimo 10 caracteres):</p>
          <textarea
            value={rejectMotivo}
            onChange={(e) => setRejectMotivo(e.target.value)}
            rows={4}
            placeholder="Describir motivo del rechazo..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancelar</Button>
            <Button
              variant="danger"
              onClick={handleReject}
              isLoading={rejectPlanilla.isPending}
              disabled={rejectMotivo.length < 10}
            >
              Rechazar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Unlock modal */}
      <Modal isOpen={showUnlockModal} onClose={() => setShowUnlockModal(false)} title="Desbloquear planilla aprobada">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Esta acción desbloqueará la planilla y la regresará a estado borrador. Se registrará en el audit log.
          </p>
          <textarea
            value={unlockJustificacion}
            onChange={(e) => setUnlockJustificacion(e.target.value)}
            rows={3}
            placeholder="Justificación para desbloquear..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowUnlockModal(false)}>Cancelar</Button>
            <Button
              variant="danger"
              onClick={handleUnlock}
              isLoading={unlockPlanilla.isPending}
              disabled={unlockJustificacion.length < 10}
            >
              Desbloquear
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Per-maestro APU + MDO detail card ─────────────────────────────

function MaestroDetailCard({
  maestroId,
  maestroNombre,
  planilla,
}: {
  maestroId: string;
  maestroNombre: string;
  planilla: PlanillaDetailDTO;
}) {
  const [expanded, setExpanded] = useState(false);

  const details = planilla.details.filter((d) => d.maestro_id === maestroId);
  const descuentos = planilla.descuentos.filter((d) => d.maestro_id === maestroId);
  const subtotalRubros = details.reduce((sum, d) => sum + d.subtotal, 0);
  const totalDescuentos = descuentos.reduce((sum, d) => sum + d.monto, 0);

  // MDO lookup per recipe for this maestro
  const maestroRates = planilla.maestros.find((m) => m.maestro_id === maestroId)?.rates ?? [];

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <p className="text-sm font-semibold text-gray-800">{maestroNombre}</p>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>Ver detalle APU / MDO</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Trabajo / MDO</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Cantidad</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Unidad</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Precio</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d) => {
                  const mdoItems = maestroRates.find((r) => r.recipe_id === d.recipe_id)?.mdo_items ?? [];
                  return (
                    <>
                      <tr key={d.id} className="border-t border-gray-100">
                        <td className="px-4 py-2 text-gray-700 font-medium">{d.recipe_nombre}</td>
                        <td className="px-4 py-2 text-right">{d.cantidad}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{d.unidad}</td>
                        <td className="px-4 py-2 text-right">${d.precio_unitario.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right font-medium">${d.subtotal.toFixed(2)}</td>
                      </tr>
                      {mdoItems.map((mdo) => (
                        <tr key={mdo.material_id} className="bg-blue-50/40">
                          <td className="px-4 py-1 pl-9 text-blue-700 text-xs italic">{mdo.material_nombre}</td>
                          <td colSpan={2} />
                          <td className="px-4 py-1 text-right text-blue-600 text-xs">${mdo.precio_unitario.toFixed(2)}</td>
                          <td />
                        </tr>
                      ))}
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-medium">
                  <td colSpan={4} className="px-4 py-2 text-right text-gray-600">Subtotal rubros</td>
                  <td className="px-4 py-2 text-right">${subtotalRubros.toFixed(2)}</td>
                </tr>
                {totalDescuentos > 0 && (
                  <tr className="bg-gray-50 text-red-600">
                    <td colSpan={4} className="px-4 py-2 text-right">Descuentos</td>
                    <td className="px-4 py-2 text-right font-medium">-${totalDescuentos.toFixed(2)}</td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>

          {/* Mobile */}
          <div className="md:hidden px-4 pb-4 pt-3 space-y-3">
            {details.map((d) => {
              const mdoItems = maestroRates.find((r) => r.recipe_id === d.recipe_id)?.mdo_items ?? [];
              return (
                <div key={d.id}>
                  <div className="flex justify-between text-sm">
                    <div>
                      <p className="text-gray-700 font-medium">{d.recipe_nombre}</p>
                      <p className="text-xs text-gray-400">{d.cantidad} {d.unidad} × ${d.precio_unitario.toFixed(2)}</p>
                    </div>
                    <span className="font-medium">${d.subtotal.toFixed(2)}</span>
                  </div>
                  {mdoItems.length > 0 && (
                    <div className="mt-1 pl-4 space-y-0.5 border-l-2 border-blue-100">
                      {mdoItems.map((mdo) => (
                        <div key={mdo.material_id} className="flex justify-between text-xs text-blue-600">
                          <span className="italic">{mdo.material_nombre}</span>
                          <span>${mdo.precio_unitario.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}
